// client/src/App.jsx
import "react-toastify/dist/ReactToastify.css";
import React, { useEffect, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ToastContainer } from "react-toastify";
import { UserProvider, UserContext } from "./contexts/UserContext";
import { VipBuyerProvider } from "./contexts/VipBuyerContext";
import { AuthProviderWithNavigate } from "./components/Auth/AuthProvider";
import AuthProviderErrorBoundary from "./components/Auth/AuthProviderErrorBoundary";
import { PermissionsProvider } from "./components/Auth/PermissionsContext";
import { PERMISSIONS } from "./utils/permissions";
import { ActivityTrackingProvider } from "./components/ActivityTracking/ActivityTrackingProvider";
import { useApiAuthInterceptor } from "./utils/apiInterceptor";
import { setLogMeta } from "./utils/logger";
import visitorTracking from "./services/VisitorTrackingService";
import AuthErrorBoundary from "./components/Auth/AuthErrorBoundary";
import { AuthStateProvider } from "./components/Auth/AuthStateProvider";
import { useAuth } from "./components/hooks/useAuth";
import AuthCallbackSpinner from "./components/Auth/AuthCallbackSpinner";
import RouteErrorBoundary from "./components/routing/RouteErrorBoundary";
import NetworkStatusBanner from "./components/network/NetworkStatusBanner";
import { initializeNetworkStatusListeners } from "./utils/networkStatus";

// ============================================================================
// EAGER IMPORTS - Loaded immediately (critical for initial page load)
// ============================================================================
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import MailivoLinkTracker from "./components/Tracking/MailivoLinkTracker";
import ProfileWarning from "./components/ProfileWarning/ProfileWarning";

// Core public pages (homepage, properties list)
import Site from "./pages/Site";
import Properties from "./pages/Properties/Properties";
import Unauthorized from "./pages/Unauthorized/Unauthorized";

// ============================================================================
// LAZY IMPORTS - Loaded on demand (reduces initial bundle size)
// ============================================================================

// Public pages (less critical)
const PropertyWithTracking = lazy(() => import("./pages/Property/PropertyWithTracking"));
const DFW = lazy(() => import("./pages/DFW/DFW"));
const Austin = lazy(() => import("./pages/Austin/Austin"));
const Houston = lazy(() => import("./pages/Houston/Houston"));
const SanAntonio = lazy(() => import("./pages/SanAntonio/SanAntonio"));
const OtherLands = lazy(() => import("./pages/OtherLands/OtherLands"));
const Financing = lazy(() => import("./pages/Financing/Financing"));
const AboutUs = lazy(() => import("./pages/AboutUs/AboutUs"));
const Support = lazy(() => import("./pages/Support/Support"));
const Sell = lazy(() => import("./pages/Sell/Sell"));
const Qualify = lazy(() => import("./pages/Qualify/Qualify"));
const Subscription = lazy(() => import("./pages/Subscription/Subscription"));
const VipSignupForm = lazy(() => import("./pages/Subscription/VipSignupForm"));
const VipSignupSuccess = lazy(() => import("./pages/Subscription/VipSignupSuccess"));
const Unsubscribe = lazy(() => import("./pages/Subscription/Unsubscribe"));
const JoinVip = lazy(() => import("./pages/JoinVip/JoinVip"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail/VerifyEmail"));
const ApprovePropertyDeletion = lazy(() => import("./pages/ApprovePropertyDeletion/ApprovePropertyDeletion"));

// Account pages
const Profile = lazy(() => import("./pages/Profile/Profile"));

// Admin layout and pages (heavy - definitely lazy load)
const AdminLayout = lazy(() => import("./components/Layout/AdminLayout"));
const Admin = lazy(() => import("./pages/Admin/Admin"));
const AddProperty = lazy(() => import("./pages/AddProperty/AddProperty"));
const EditProperty = lazy(() => import("./pages/EditProperty/EditProperty"));
const AdminProperties = lazy(() => import("./pages/AdminProperties/AdminProperties"));
const AdminUsers = lazy(() => import("./pages/AdminUsers/AdminUsers"));
const AdminDetail = lazy(() => import("./pages/AdminUsers/components/AdminDetail/AdminDetail"));
const EditAdminDetail = lazy(() => import("./pages/AdminUsers/components/EditAdminDetail/EditAdminDetail"));
const AdminBuyers = lazy(() => import("./pages/AdminBuyers/AdminBuyers"));
const BuyerDetail = lazy(() => import("./components/BuyerDetail/BuyerDetail"));
const CreateBuyer = lazy(() => import("./pages/AdminBuyers/components/CreateBuyer/CreateBuyer"));
const EditBuyer = lazy(() => import("./pages/AdminBuyers/components/EditBuyer/EditBuyer"));
const BuyerOffers = lazy(() => import("./components/BuyerOffers/BuyerOffers"));
const EmailLists = lazy(() => import("./components/EmailLists/EmailLists"));
const SegmentEmailLists = lazy(() => import("./components/EmailLists/SegmentEmailLists"));
const AdminDeals = lazy(() => import("./pages/AdminDeals/AdminDeals"));
const AdminOffer = lazy(() => import("./pages/AdminOffer/AdminOffer"));
const Offer = lazy(() => import("./pages/AdminOffer/Offer"));
const AdminFinancing = lazy(() => import("./pages/AdminFinancing/AdminFinancing"));
const AdminSettings = lazy(() => import("./pages/AdminSettings/AdminSettings"));
const AdminHelp = lazy(() => import("./pages/AdminHelp/AdminHelp"));
const AdminPractor = lazy(() => import("./pages/AdminPractor/AdminPractor"));
const AdminPractorPropertyDetail = lazy(() => import("./pages/AdminPractorPropertyDetail/AdminPractorPropertyDetail"));
const AdminPractorRuns = lazy(() => import("./pages/AdminPractorRuns/AdminPractorRuns"));
const TrafficPage = lazy(() => import("./pages/Traffic/TrafficPage"));
const Discount = lazy(() => import("./pages/Discount/Discount"));
const MailMerge = lazy(() => import("./pages/MailMerge/MailMerge"));
const PropertyUploadSendPage = lazy(() => import("./pages/PropertySend/PropertyUploadSendPage"));
const PropertyDiscountSendPage = lazy(() => import("./pages/PropertySend/PropertyDiscountSendPage"));

// Components (heavy)
const OfferTable = lazy(() => import("./components/OfferTable/OfferTable"));
const CreateDealForm = lazy(() => import("@/components/Deal/CreateDealForm"));
const DealDetail = lazy(() => import("@/components/Deal/DealDetail"));
const DealFinancialSummary = lazy(() => import("@/components/Deal/DealFinancialSummary"));
const DealsList = lazy(() => import("@/components/Deal/DealsList"));
const PaymentList = lazy(() => import("@/components/Deal/PaymentList"));

// Dev tools - only load in development
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() => import("react-query/devtools").then(m => ({ default: m.ReactQueryDevtools })))
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 5 * 60 * 1000, // Increased from 2min to 5min for better caching
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

// Wrapper for lazy-loaded routes
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// Create a wrapper component to apply the API interceptor
function ApiAuthProvider({ children }) {
  useApiAuthInterceptor();
  return children;
}

// Visitor tracking wrapper component
function TrackingProvider({ children }) {
  const location = useLocation();

  // Initialize visitor tracking
  useEffect(() => {
    visitorTracking.startTracking();
    return () => visitorTracking.stopTracking();
  }, []);

  // Track page changes
  useEffect(() => {
    visitorTracking.trackPageView(location.pathname);
  }, [location.pathname]);

  return children;
}

function LogMetaProvider({ children }) {
  const location = useLocation();
  const userContext = useContext(UserContext);
  const currentUser = userContext?.currentUser;
  const {
    authLifecycleState,
    authReadyForProtectedRoutes,
    authResolved,
    isAuthenticated,
    sessionExpired,
    identityUser,
  } = useAuth();

  const dbAdminId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
  const identityId = identityUser?.sub || null;
  const authState =
    sessionExpired || authLifecycleState === "session_expired"
      ? "session_expired"
      : authLifecycleState === "error"
      ? "auth_error"
      : !authResolved
      ? "auth_booting"
      : !isAuthenticated
      ? "guest"
      : dbAdminId
      ? "authenticated_profiled"
      : identityId
      ? "authenticated_no_profile"
      : "authenticated_unknown";

  useEffect(() => {
    setLogMeta({
      identityId,
      authLifecycleState: authLifecycleState || null,
      authReadyForProtectedRoutes: Boolean(authReadyForProtectedRoutes),
      authResolved: Boolean(authResolved),
      authState,
      dbAdminId,
      isAuthenticated: Boolean(isAuthenticated),
      route: location.pathname,
      userId: dbAdminId || identityId || null,
      role: currentUser?.role || null,
    });
  }, [
    identityId,
    authLifecycleState,
    authReadyForProtectedRoutes,
    authResolved,
    authState,
    location.pathname,
    dbAdminId,
    isAuthenticated,
    currentUser?.role,
    sessionExpired,
  ]);

  return children;
}

function App() {
  useEffect(() => {
    return initializeNetworkStatusListeners();
  }, []);

  useEffect(() => {
    const getButtonTooltip = (button) => {
      const explicit = button.getAttribute("data-tooltip");
      if (explicit) return explicit;
      const ariaLabel = button.getAttribute("aria-label");
      if (ariaLabel) return ariaLabel;
      const existingTitle = button.getAttribute("title");
      if (existingTitle) return existingTitle;
      const text = button.textContent?.replace(/\s+/g, " ").trim();
      if (text) return text;
      return button.getAttribute("name") || button.getAttribute("id") || button.value || "";
    };

    const applyButtonTooltips = (root = document) => {
      root.querySelectorAll("button").forEach((button) => {
        const tooltip = getButtonTooltip(button);
        if (!tooltip) return;
        if (button.getAttribute("title") !== tooltip) {
          button.setAttribute("title", tooltip);
        }
      });
    };

    applyButtonTooltips();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.("button")) {
            const tooltip = getButtonTooltip(node);
            if (tooltip && node.getAttribute("title") !== tooltip) {
              node.setAttribute("title", tooltip);
            }
          }
          if (node.querySelectorAll) {
            applyButtonTooltips(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <UserProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProviderErrorBoundary>
            <AuthProviderWithNavigate>
              <AuthStateProvider>
                <AuthCallbackSpinner />
                <AuthErrorBoundary>
                  <PermissionsProvider>
                    <ApiAuthProvider>
                      <VipBuyerProvider>
                        <ActivityTrackingProvider>
                          <TrackingProvider>
                            <LogMetaProvider>
                              <ScrollToTop />
                              <MailivoLinkTracker />
                              <ProfileWarning />
                              <NetworkStatusBanner />
                              <Routes>
                              {/* Public routes - accessible to all */}
                              <Route element={<Layout />}>
                                <Route path="/" element={<Site />} />
                                <Route path="/properties">
                                  <Route index element={<Properties />} />
                                  <Route path=":propertyId" element={<LazyRoute><PropertyWithTracking /></LazyRoute>} />
                                  <Route path=":propertyId/offers" element={<LazyRoute><OfferTable /></LazyRoute>} />
                                  <Route path=":propertyId/qualify" element={<LazyRoute><Qualify /></LazyRoute>} />
                                </Route>
                                <Route path="/sell" element={<LazyRoute><Sell /></LazyRoute>} />
                                <Route path="/financing" element={<LazyRoute><Financing /></LazyRoute>} />
                                <Route path="/support" element={<LazyRoute><Support /></LazyRoute>} />
                                <Route path="/about-us" element={<LazyRoute><AboutUs /></LazyRoute>} />
                                <Route path="/subscription" element={<LazyRoute><Subscription /></LazyRoute>} />
                                <Route path="/vip-signup" element={<LazyRoute><VipSignupForm /></LazyRoute>} />
                                <Route path="/vip-signup-success" element={<LazyRoute><VipSignupSuccess /></LazyRoute>} />
                                <Route path="/DFW" element={<LazyRoute><DFW /></LazyRoute>} />
                                <Route path="/Austin" element={<LazyRoute><Austin /></LazyRoute>} />
                                <Route path="/Houston" element={<LazyRoute><Houston /></LazyRoute>} />
                                <Route path="/SanAntonio" element={<LazyRoute><SanAntonio /></LazyRoute>} />
                                <Route path="/OtherLands" element={<LazyRoute><OtherLands /></LazyRoute>} />
                                <Route path="/unauthorized" element={<Unauthorized />} />
                                <Route path="/property-deletion/:token" element={<LazyRoute><ApprovePropertyDeletion /></LazyRoute>} />
                                <Route path="/join-vip" element={<LazyRoute><JoinVip /></LazyRoute>} />
                                <Route path="/verify-email" element={<LazyRoute><VerifyEmail /></LazyRoute>} />
                                {/* Unsubscribe route - standalone without main layout */}
                                <Route path="/unsubscribe/:id" element={<LazyRoute><Unsubscribe /></LazyRoute>} />

                                {/* Protected route for profile */}
                                <Route
                                  path="/profile"
                                  element={
                                    <ProtectedRoute>
                                      <LazyRoute><Profile /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/qualify" element={<LazyRoute><Qualify /></LazyRoute>} />
                              </Route>
                              <Route
                                path="/traffic"
                                element={
                                  <RouteErrorBoundary routeName="traffic-route">
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCESS_TRAFFIC]}>
                                      <LazyRoute><TrafficPage /></LazyRoute>
                                    </ProtectedRoute>
                                  </RouteErrorBoundary>
                                }
                              />
                              {/* Admin routes - using permissions instead of roles */}
                              <Route
                                path="/admin"
                                element={
                                  <RouteErrorBoundary routeName="admin-route">
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCESS_ADMIN]}>
                                      <LazyRoute><AdminLayout /></LazyRoute>
                                    </ProtectedRoute>
                                  </RouteErrorBoundary>
                                }>
                                <Route index element={<LazyRoute><Admin /></LazyRoute>} />
                                <Route path="add-property" element={<LazyRoute><AddProperty /></LazyRoute>} />
                                <Route path="add-property/:propertyId/send" element={<LazyRoute><PropertyUploadSendPage /></LazyRoute>} />
                                <Route path="edit-property/:propertyId" element={<LazyRoute><EditProperty /></LazyRoute>} />
                                <Route path="offers" element={<LazyRoute><AdminOffer /></LazyRoute>} />
                                <Route path="offers/id/:offerId" element={<LazyRoute><Offer /></LazyRoute>} />
                                <Route path="properties" element={<LazyRoute><AdminProperties /></LazyRoute>} />
                                <Route path="practor" element={<LazyRoute><AdminPractor /></LazyRoute>} />
                                <Route path="practor/properties/:propertyId" element={<LazyRoute><AdminPractorPropertyDetail /></LazyRoute>} />
                                <Route path="practor/runs" element={<LazyRoute><AdminPractorRuns /></LazyRoute>} />
                                {/* Discount */}
                                <Route path="edit-property/:propertyId/discount" element={<LazyRoute><Discount /></LazyRoute>} />
                                <Route path="edit-property/:propertyId/discount/send" element={<LazyRoute><PropertyDiscountSendPage /></LazyRoute>} />

                                {/* Deals */}
                                <Route path="deals" element={<LazyRoute><AdminDeals /></LazyRoute>} />
                                <Route path="deals/list" element={<LazyRoute><DealsList /></LazyRoute>} />
                                <Route path="deals/create" element={<LazyRoute><CreateDealForm /></LazyRoute>} />
                                <Route path="deals/:id" element={<LazyRoute><DealDetail /></LazyRoute>} />
                                <Route path="deals/:id/payments" element={<LazyRoute><PaymentList /></LazyRoute>} />
                                <Route path="deals/:id/summary" element={<LazyRoute><DealFinancialSummary /></LazyRoute>} />
                                <Route path="mailmerge" element={<LazyRoute><MailMerge /></LazyRoute>} />

                                {/* Admins */}
                                <Route
                                  path="admins"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_ADMINS]}>
                                      <LazyRoute><AdminUsers /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="admins/:adminId"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_ADMINS]}>
                                      <LazyRoute><AdminDetail /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="admins/:adminId/edit"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.WRITE_ADMINS]}>
                                      <LazyRoute><EditAdminDetail /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Buyers */}
                                <Route
                                  path="buyers"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_BUYERS]}>
                                      <LazyRoute><AdminBuyers /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="buyers/create"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.WRITE_BUYERS]}>
                                      <LazyRoute><CreateBuyer /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="buyers/:buyerId"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_BUYERS]}>
                                      <LazyRoute><BuyerDetail /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="buyers/:buyerId/edit"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.WRITE_BUYERS]}>
                                      <LazyRoute><EditBuyer /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="buyers/:buyerId/offers"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_OFFERS]}>
                                      <LazyRoute><BuyerOffers /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="email-lists"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.READ_BUYERS]}>
                                      <LazyRoute><EmailLists /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="segment"
                                  element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.WRITE_BUYERS]}>
                                      <LazyRoute><SegmentEmailLists /></LazyRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                {/* Qualification dashboard */}
                                <Route path="financing" element={<LazyRoute><AdminFinancing /></LazyRoute>} />

                                {/* Settings */}
                                <Route path="settings" element={<LazyRoute><AdminSettings /></LazyRoute>} />

                                {/* Help */}
                                <Route path="help" element={<LazyRoute><AdminHelp /></LazyRoute>} />
                              </Route>

                              {/* Agent routes - using permissions instead of roles */}
                              <Route
                                path="/agent"
                                element={
                                    <RouteErrorBoundary routeName="agent-route">
                                    <ProtectedRoute
                                      requiredPermissions={[PERMISSIONS.WRITE_PROPERTIES]}
                                      unauthenticatedAction="login"
                                    >
                                      <AdminLayout />
                                    </ProtectedRoute>
                                  </RouteErrorBoundary>
                                }></Route>
                              </Routes>
                              <ToastContainer />
                            </LogMetaProvider>
                          </TrackingProvider>
                        </ActivityTrackingProvider>
                      </VipBuyerProvider>
                    </ApiAuthProvider>
                  </PermissionsProvider>
                </AuthErrorBoundary>
              </AuthStateProvider>
            </AuthProviderWithNavigate>
          </AuthProviderErrorBoundary>
        </BrowserRouter>
        {/* Only load React Query Devtools in development */}
        {import.meta.env.DEV && ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </QueryClientProvider>
    </UserProvider>
  );
}

export default App;


